import { useState, useCallback } from 'react';
import { clientsAPI, clientPipelinesAPI } from '../services/api';
import { useToast } from './use-toast';

export const usePipelineClients = (selectedPipeline) => {
  const [pipelineClients, setPipelineClients] = useState({});
  const [allClients, setAllClients] = useState([]);
  const [clientAssignments, setClientAssignments] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [addClientDialog, setAddClientDialog] = useState(false);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState('');
  const [selectedAssigneeToAdd, setSelectedAssigneeToAdd] = useState('');
  const [forSpouse, setForSpouse] = useState(false);
  const [selectedClientHasSpouse, setSelectedClientHasSpouse] = useState(false);
  const [deleteClientDialog, setDeleteClientDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const { toast } = useToast();

  const fetchAllClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll({});
      setAllClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  const fetchPipelineClients = useCallback(async () => {
    if (!selectedPipeline) return;

    try {
      const response = await clientPipelinesAPI.getAll({ pipelineId: selectedPipeline._id });
      const assignments = response.data.assignments || response.data.clientPipelines || [];
      setClientAssignments(assignments);

      // Group clients by stage
      const grouped = {};
      selectedPipeline.stages.forEach(stage => {
        grouped[stage.id] = [];
      });

      assignments.forEach(assignment => {
        if (assigneeFilter !== 'all' && assignment.assignedTo !== assigneeFilter) {
          return;
        }

        const client = allClients.find(c => c._id === assignment.clientId);
        if (client && grouped[assignment.stageId]) {
          grouped[assignment.stageId].push({
            ...client,
            assignmentId: assignment._id,
            assignedTo: assignment.assignedTo || '',
            completedStageTasks: assignment.completedStageTasks || {},
            forSpouse: assignment.forSpouse || false,
            displayName: assignment.forSpouse ? (client.spouse?.name || client.name) : client.name
          });
        }
      });

      setPipelineClients(grouped);
    } catch (error) {
      console.error('Error fetching pipeline clients:', error);
    }
  }, [selectedPipeline, allClients, assigneeFilter]);

  const handleClientSelectForAdd = useCallback((clientId) => {
    setSelectedClientToAdd(clientId);
    const client = allClients.find(c => c._id === clientId);
    setSelectedClientHasSpouse(client?.type === 'Individual' && client?.spouse?.name);
    setForSpouse(false);
  }, [allClients]);

  const handleAddClientToPipeline = useCallback(async () => {
    if (!selectedClientToAdd || !selectedPipeline) {
      toast({ title: 'Error', description: 'Please select a client', variant: 'destructive' });
      return;
    }

    try {
      await clientPipelinesAPI.create({
        clientId: selectedClientToAdd,
        pipelineId: selectedPipeline._id,
        stageId: selectedPipeline.stages[0].id,
        assignedTo: selectedAssigneeToAdd === 'unassigned' ? '' : (selectedAssigneeToAdd || ''),
        forSpouse: forSpouse
      });

      const client = allClients.find(c => c._id === selectedClientToAdd);
      const displayName = forSpouse && client?.spouse?.name ? client.spouse.name : client?.name;
      toast({ title: 'Success', description: `Added ${displayName} to pipeline` });
      setAddClientDialog(false);
      setSelectedClientToAdd('');
      setSelectedAssigneeToAdd('');
      setForSpouse(false);
      setSelectedClientHasSpouse(false);
      fetchPipelineClients();
    } catch (error) {
      console.error('Error adding client to pipeline:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add client', variant: 'destructive' });
    }
  }, [selectedClientToAdd, selectedPipeline, selectedAssigneeToAdd, forSpouse, allClients, toast, fetchPipelineClients]);

  const openDeleteClientDialog = useCallback((client) => {
    setClientToDelete(client);
    setDeleteClientDialog(true);
  }, []);

  const handleDeleteClientFromPipeline = useCallback(async () => {
    if (!clientToDelete || !clientToDelete.assignmentId) return;

    try {
      await clientPipelinesAPI.delete(clientToDelete.assignmentId);
      toast({ title: 'Success', description: `${clientToDelete.displayName} removed from pipeline` });
      setDeleteClientDialog(false);
      setClientToDelete(null);
      fetchPipelineClients();
    } catch (error) {
      console.error('Error removing client from pipeline:', error);
      toast({ title: 'Error', description: 'Failed to remove client from pipeline', variant: 'destructive' });
    }
  }, [clientToDelete, toast, fetchPipelineClients]);

  const handleTaskToggle = useCallback(async (client, stageId, taskId, isCompleted) => {
    try {
      const assignmentId = client.assignmentId;
      if (!assignmentId) return;

      if (isCompleted) {
        const response = await clientPipelinesAPI.uncompleteTask(assignmentId, { stageId, taskId });
        if (response.data.message) {
          toast({ title: 'Success', description: 'Task uncompleted' });
        }
      } else {
        const response = await clientPipelinesAPI.completeTask(assignmentId, { stageId, taskId });
        if (response.data.autoAdvanced) {
          toast({
            title: 'Stage Complete!',
            description: `${client.displayName} automatically moved to next stage`
          });
        }
      }
      fetchPipelineClients();
    } catch (error) {
      console.error('Error updating task:', error);
      toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' });
    }
  }, [toast, fetchPipelineClients]);

  return {
    pipelineClients,
    allClients,
    clientAssignments,
    assigneeFilter,
    setAssigneeFilter,
    addClientDialog,
    setAddClientDialog,
    selectedClientToAdd,
    selectedAssigneeToAdd,
    setSelectedAssigneeToAdd,
    forSpouse,
    setForSpouse,
    selectedClientHasSpouse,
    deleteClientDialog,
    setDeleteClientDialog,
    clientToDelete,
    fetchAllClients,
    fetchPipelineClients,
    handleClientSelectForAdd,
    handleAddClientToPipeline,
    openDeleteClientDialog,
    handleDeleteClientFromPipeline,
    handleTaskToggle
  };
};
